/* ==========================================================================
   MONTAGE-THEME · <mt-slider>
   --------------------------------------------------------------------------
   Eine horizontale Spur mit scroll-snap. Ohne JavaScript bleibt sie eine
   normale Scrollflaeche und ist mit Wischen und Tastatur bedienbar. Erst
   dieses Skript ergaenzt Pfeile, Punkte, Zaehler und optionales Weiterlaufen.

   Deshalb steht die Bedienleiste im Markup auf hidden: Knoepfe, die ohne
   Skript nichts tun, duerfen gar nicht erst dastehen.

   Erwartetes Markup:
     <mt-slider data-autoplay="6000">
       <div data-spur> <div data-folie>…</div> … </div>
       <div data-steuerung hidden>
         <button data-zurueck>…</button>
         <div data-punkte></div>          (optional)
         <div data-zaehler></div>         (optional)
         <button data-pause>…</button>    (optional, nur mit data-autoplay)
         <button data-weiter>…</button>
       </div>
     </mt-slider>
   ========================================================================== */
/* Gekapselt in einen sofort ausgefuehrten Ausdruck, weil mehrere Sektionen
   dieselbe Datei einbinden koennen. Zwei script-Tags mit derselben Adresse
   fuehrt der Browser zweimal aus, und eine Klasse im Modulrumpf wirft beim
   zweiten Mal "has already been declared". Innerhalb der Kapsel ist die
   zweite Ausfuehrung folgenlos, die Registrierungspruefung darunter faengt
   den Rest ab. */
(function () {
  class MontageSlider extends HTMLElement {
    /* Der Text kommt aus dem Datenattribut, das mt-texte.liquid setzt, und
       damit aus locales/*.json. Die Platzhalter fuellt erst das Skript, weil
       erst hier die Zahlen bekannt sind. Der englische Rueckfalltext greift
       nur, wenn das Attribut fehlt, und faellt dann bewusst auf. */
    folienText(i) {
      const vorlage = this.dataset.textFolie || 'Go to slide {{ index }} of {{ anzahl }}';
      return vorlage
        .replace('{{ index }}', i + 1)
        .replace('{{ anzahl }}', this.folien.length);
    }

    connectedCallback() {
      this.spur = this.querySelector('[data-spur]');
      this.folien = Array.from(this.querySelectorAll('[data-folie]'));
      this.steuerung = this.querySelector('[data-steuerung]');
      if (!this.spur || this.folien.length < 2) return;

      this.zurueck = this.querySelector('[data-zurueck]');
      this.weiter = this.querySelector('[data-weiter]');
      this.punkteRaum = this.querySelector('[data-punkte]');
      this.zaehlerRaum = this.querySelector('[data-zaehler]');
      this.pause = this.querySelector('[data-pause]');
      this.index = 0;

      // Alle Bedienleisten enthuellen, auch seitliche Pfeile auf der Folie
      this.querySelectorAll('[data-steuerung]').forEach((s) => s.removeAttribute('hidden'));
      this.setAttribute('data-bereit', '');

      this.zurueck?.addEventListener('click', () => this.springe(this.index - 1, true));
      this.weiter?.addEventListener('click', () => this.springe(this.index + 1, true));

      this.bauePunkte();
      this.baueZaehler();

      // Scrollposition ist die Wahrheit, nicht der interne Zaehler: der Nutzer
      // kann jederzeit selbst wischen.
      let ruhe;
      this.spur.addEventListener('scroll', () => {
        clearTimeout(ruhe);
        ruhe = setTimeout(() => this.lieszurueck(), 90);
      }, { passive: true });

      this.spur.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.springe(this.index + 1, true); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.springe(this.index - 1, true); }
      });

      this.ziehenEinrichten();

      this.starteLauf();
      this.aktualisiere();
    }

    disconnectedCallback() {
      this.haltAn();
    }

    /* ── Ziehen mit Maus und Finger ─────────────────────────────────────
       Am Handy scrollt die Spur von selbst (overflow-x plus Touch). Mit der
       MAUS geht das nicht: waagerecht scrollen verlangt sonst Shift plus
       Rad oder eine Trackpad-Geste. Deshalb hier Ziehen mit gedrueckter
       Maustaste, wie es die Vorlage auch tut.

       Der Knackpunkt: ein Ziehen darf keinen Klick ausloesen, sonst oeffnet
       beim Wischen ungewollt das Vollbild. Ueberschreitet die Bewegung die
       Schwelle, wird der naechste Klick einmalig abgefangen. */
    ziehenEinrichten() {
      const SCHWELLE = 6;
      let aktiv = false;
      let startX = 0;
      let startScroll = 0;
      let bewegt = false;

      const runter = (e) => {
        // Nur die primaere Maustaste, Finger laesst der Browser selbst scrollen
        if (e.pointerType === 'touch' || e.button !== 0) return;
        aktiv = true;
        bewegt = false;
        startX = e.clientX;
        startScroll = this.spur.scrollLeft;
        this.spur.classList.add('is-ziehen');
      };

      const bewegen = (e) => {
        if (!aktiv) return;
        const weg = e.clientX - startX;
        if (Math.abs(weg) > SCHWELLE) bewegt = true;
        if (bewegt) {
          e.preventDefault();
          this.spur.scrollLeft = startScroll - weg;
          this.haltAn();
        }
      };

      const hoch = () => {
        if (!aktiv) return;
        aktiv = false;
        this.spur.classList.remove('is-ziehen');
        if (bewegt) {
          // Genau einen Klick schlucken, damit das Wischen nichts oeffnet
          this.spur.addEventListener('click', (e) => {
            e.stopPropagation();
            e.preventDefault();
          }, { capture: true, once: true });
        }
      };

      this.spur.addEventListener('pointerdown', runter);
      this.spur.addEventListener('pointermove', bewegen);
      window.addEventListener('pointerup', hoch);
      window.addEventListener('pointercancel', hoch);
      // Bilder und Links nicht als Datei mitziehen
      this.spur.addEventListener('dragstart', (e) => e.preventDefault());
    }

    /* ── Bedienelemente ─────────────────────────────────────────────────── */
    bauePunkte() {
      if (!this.punkteRaum) return;
      // ⚠️ Leeren, BEVOR gebaut wird. Im Theme-Editor setzt Shopify eine
      //    Sektion beim Bearbeiten neu ins Dokument, und connectedCallback
      //    laeuft dann ein zweites Mal. Ohne diese Zeile stehen die Punkte
      //    doppelt da. Im Laden faellt das nie auf, im Editor sofort.
      this.punkteRaum.replaceChildren();
      this.punkte = this.folien.map((_, i) => {
        const knopf = document.createElement('button');
        knopf.type = 'button';
        knopf.className = 'mt-slider__punkt';
        knopf.setAttribute('aria-label', this.folienText(i));
        knopf.addEventListener('click', () => this.springe(i, true));
        this.punkteRaum.appendChild(knopf);
        return knopf;
      });
    }

    baueZaehler() {
      if (!this.zaehlerRaum) return;
      // Gleicher Grund wie bei bauePunkte: sonst steht im Theme-Editor
      // "01 — 02 01 — 02" statt "01 — 02".
      this.zaehlerRaum.replaceChildren();
      this.zaehler = this.folien.map((_, i) => {
        const knopf = document.createElement('button');
        knopf.type = 'button';
        knopf.className = 'mt-slider__zaehler-knopf mt-zahl';
        knopf.textContent = String(i + 1).padStart(2, '0');
        knopf.setAttribute('aria-label', this.folienText(i));
        knopf.addEventListener('click', () => this.springe(i, true));
        this.zaehlerRaum.appendChild(knopf);
        return knopf;
      });
      // Der Ladestrich der Vorlage: sitzt direkt hinter der aktiven Zahl und
      // fuellt sich ueber die Autoplay-Dauer, dann wechselt die Folie.
      this.strich = document.createElement('span');
      this.strich.className = 'mt-slider__strich';
      this.strich.setAttribute('aria-hidden', 'true');
      this.strich.innerHTML = '<span></span>';
    }

    zeichneStrich() {
      if (!this.strich || !this.zaehler) return;
      const aktiv = this.zaehler[this.index];
      aktiv.insertAdjacentElement('afterend', this.strich);
      const balken = this.strich.firstElementChild;
      // Neu anstossen: ohne Uebergang auf 0, dann linear auf 100 % in Taktzeit.
      balken.style.transition = 'none';
      balken.style.width = '0%';
      if (this.laeuft && this.takt) {
        void balken.offsetWidth;
        balken.style.transition = `width ${this.takt}ms linear`;
        balken.style.width = '100%';
      }
    }

    /* ── Bewegung ───────────────────────────────────────────────────────── */
    springe(ziel, vomNutzer = false) {
      const letzte = this.folien.length - 1;
      if (ziel < 0) ziel = letzte;
      if (ziel > letzte) ziel = 0;
      this.index = ziel;
      this.spur.scrollTo({ left: this.folien[ziel].offsetLeft - this.spur.offsetLeft, behavior: 'smooth' });
      this.aktualisiere();
      // Wer selbst bedient, will nicht weitergeschoben werden.
      if (vomNutzer) this.haltAn();
    }

    lieszurueck() {
      const mitte = this.spur.scrollLeft + this.spur.clientWidth / 2;
      let naechste = 0;
      let abstand = Infinity;
      this.folien.forEach((folie, i) => {
        const folienMitte = folie.offsetLeft - this.spur.offsetLeft + folie.offsetWidth / 2;
        const d = Math.abs(folienMitte - mitte);
        if (d < abstand) { abstand = d; naechste = i; }
      });
      if (naechste !== this.index) {
        this.index = naechste;
        this.aktualisiere();
      }
    }

    aktualisiere() {
      this.punkte?.forEach((p, i) => {
        p.classList.toggle('is-aktiv', i === this.index);
        p.setAttribute('aria-current', i === this.index ? 'true' : 'false');
      });
      this.zaehler?.forEach((z, i) => {
        z.classList.toggle('is-aktiv', i === this.index);
        z.setAttribute('aria-current', i === this.index ? 'true' : 'false');
      });
      this.zeichneStrich();
      this.folien.forEach((f, i) => {
        // data-aktiv treibt den Texteinlauf der Folie (CSS [data-einlauf]).
        f.toggleAttribute('data-aktiv', i === this.index);
        // Nicht sichtbare Folien aus der Tabreihenfolge nehmen, sonst springt
        // der Fokus in Inhalte, die niemand sieht.
        const drin = f.querySelectorAll('a, button, input, select, textarea');
        drin.forEach((el) => el.setAttribute('tabindex', i === this.index ? '0' : '-1'));
      });
    }

    /* ── Weiterlaufen ───────────────────────────────────────────────────── */
    starteLauf() {
      const takt = parseInt(this.dataset.autoplay || '0', 10);
      if (!takt || !this.pause) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.pause.hidden = true;
        return;
      }
      this.takt = takt;
      this.laeuft = false;
      this.pause.addEventListener('click', () => (this.laeuft ? this.haltAn() : this.lauf()));
      this.addEventListener('mouseenter', () => this.haltAn());
      this.addEventListener('focusin', () => this.haltAn());
      this.lauf();
    }

    lauf() {
      if (!this.takt) return;
      clearInterval(this.uhr);
      this.uhr = setInterval(() => this.springe(this.index + 1), this.takt);
      this.laeuft = true;
      this.pause?.setAttribute('aria-label', window.accessibilityStrings?.pauseSlideshow || 'Pause slideshow');
      this.pause?.setAttribute('data-zustand', 'laeuft');
      this.zeichneStrich();
    }

    haltAn() {
      clearInterval(this.uhr);
      this.laeuft = false;
      this.pause?.setAttribute('aria-label', window.accessibilityStrings?.playSlideshow || 'Play slideshow');
      this.pause?.setAttribute('data-zustand', 'haelt');
      // Balken an Ort und Stelle einfrieren
      const balken = this.strich?.firstElementChild;
      if (balken) {
        const breite = getComputedStyle(balken).width;
        balken.style.transition = 'none';
        balken.style.width = breite;
      }
    }
  }

  if (!customElements.get('mt-slider')) {
    customElements.define('mt-slider', MontageSlider);
  }
})();
